import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from '../../../hooks/useAuthUser';
import { IContent } from '../../../interfaces';
import { formatDateString } from '../../../shared/utility';
import { ETranslation } from '../../../translations/translation-keys';
import Table from '../../UI/Table/Table';
import TableTh from '../../UI/Table/TableTh/TableTh';
import { useTableSort } from '../../UI/Table/useTableSort';
import classes from './ContentsTable.module.scss';

interface IProps {
  contents: Array<IContent>
  statusProp?: string;
  onClick: (event: React.MouseEvent<HTMLTableRowElement>, id: string) => void;
  sortable?: boolean;
  containerClassName?: string;
  containerStyle?: CSSProperties;
  isPrimary?: boolean;
}

const ContentTable: React.FC<IProps> = ({ contents, statusProp, onClick, sortable, containerClassName, containerStyle, isPrimary }) => {
  const { t } = useTranslation();
  const authUser = useAuthUser();
  const { sort, onSort, items: sortedContents } = useTableSort({ data: contents });

  return (
    <div className={containerClassName} style={containerStyle}>
      <Table hover sortContext={{ sort, onSort }}>
        <thead>
          <tr className={classes.TableHeader}>
            {statusProp}
            {sortable ? (
              <>
                {isPrimary ? <TableTh sort="operator.name">{t(ETranslation.COMMON_OPERATOR_NAME)}</TableTh> : null}
                <TableTh sort="created" type="date">{t(ETranslation.COMMON_CREATED)}</TableTh>
                <th>{t(ETranslation.COMMON_SERVICE)}</th>
                <TableTh sort="title">{t(ETranslation.CONTENT_NAME)}</TableTh>
                <TableTh sort="shortContent">{t(ETranslation.CONTENT_SHORTCONTENT)}</TableTh>
                <TableTh sort="url">{t(ETranslation.COMMON_WWW)}</TableTh>
                <th>{t(ETranslation.CONTENT_TAGS)}</th>
                <TableTh sort="publishDate" type="date">{t(ETranslation.CONTENT_DATE)}</TableTh>
                <TableTh sort="showPublishDate" type="boolean">{t(ETranslation.CONTENT_SHOWDATE)}</TableTh>
              </>
            ) : (
              <>
                {isPrimary ? <th>{t(ETranslation.COMMON_OPERATOR_NAME)}</th> : null}
                <th>{t(ETranslation.COMMON_CREATED)}</th>
                <th>{t(ETranslation.COMMON_SERVICE)}</th>
                <th>{t(ETranslation.CONTENT_NAME)}</th>
                <th>{t(ETranslation.CONTENT_SHORTCONTENT)}</th>
                <th>{t(ETranslation.COMMON_WWW)}</th>
                <th>{t(ETranslation.CONTENT_TAGS)}</th>
                <th>{t(ETranslation.CONTENT_DATE)}</th>
                <th>{t(ETranslation.CONTENT_SHOWDATE)}</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedContents.map(content => (
            <tr key={content.id} onClick={(e) => onClick(e, content.id)}>
              {authUser?.isPrimary && <td>{content.operator?.name}</td>}
              <td>{content.created && formatDateString(content.created)}</td>
              <td>{content.services?.map(s => s.name).join(", ")}</td>
              <td>{content.title}</td>
              <td>{content.shortContent}</td>
              <td>{content.url}</td>
              <td>{content.tags?.map(c => c).join(", ")}</td>
              <td>{content.publishDate && formatDateString(content.publishDate)}</td>
              <td>{content.showPublishDate ? <FontAwesomeIcon icon={faCheck} /> : null}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default ContentTable;
